import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import React from 'react';

import {PanelBankContextProvider} from '../../state/panelbank/context';
import {useRunSelectorResizingContextMaybe} from '../MultiRunWorkspace/RunSelectorResizingContext';
import {useAutoSyncPanelBankContext} from '../PanelBank/AutoSyncPanelBankContext';
import {useWorkspaceInheritedSettingsForPanel} from '../WorkspaceDrawer/Settings/hooks/useWorkspaceInheritedSettingsForPanel';
import {SectionSettingsContextProvider} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {EditablePanelFullScreenWrapper} from './EditablePanelFullScreenWrapper';
import {FullScreenPanelNotFound} from './FullScreenPanelNotFound';
import {useConfigXBounds} from './hooks';
import {PanelContextProvider} from './PanelContextProvider';
import {SharedPanelStateContextProvider} from './SharedPanelStateContext';
import {useStandalonePanelContext} from './StandalonePanel';

export const FullScreenPanelContainer = ({
  isProjectReadOnly,
}: {
  isProjectReadOnly: boolean; // whether user can edit project level resources (e.g. reports)
}) => {
  const {isPanelNotFound, panelRefAndPanelBankSectionConfigRef} =
    useStandalonePanelContext();

  const {isLoading: isAutoSyncLoading} = useAutoSyncPanelBankContext();
  const isLoading = isPanelNotFound && isAutoSyncLoading;

  if (isLoading) {
    return <WaveLoader size="small" />;
  }

  if (isPanelNotFound) {
    return <FullScreenPanelNotFound />;
  }

  const {panelBankSectionConfigRef} = panelRefAndPanelBankSectionConfigRef!;

  return (
    // We need to wrap the FullScreenPanel component in a SectionSettingsContextProvider so
    // inherited settings takes into both workspace and section values
    <SectionSettingsContextProvider sectionRef={panelBankSectionConfigRef}>
      <PanelBankContextProvider>
        <FullScreenPanel isProjectReadOnly={isProjectReadOnly} />
      </PanelBankContextProvider>
    </SectionSettingsContextProvider>
  );
};

const FullScreenPanel = ({isProjectReadOnly}: {isProjectReadOnly: boolean}) => {
  const {panelRefAndPanelBankSectionConfigRef} = useStandalonePanelContext();
  const {panelBankSectionConfigRef, panelRef} =
    panelRefAndPanelBankSectionConfigRef!;

  // Make sure the SectionSettingsContextProvider wraps his FullScreenPanel component
  const inheritedSettings = useWorkspaceInheritedSettingsForPanel();
  const configXBounds = useConfigXBounds(panelRef);
  const runSelectorResizingContext = useRunSelectorResizingContextMaybe();
  const {shouldHidePanelContents} = useWorkspaceDrawerContext();
  const shouldRenderContent =
    !shouldHidePanelContents && !runSelectorResizingContext?.isResizing;

  return (
    <SharedPanelStateContextProvider config={configXBounds}>
      <PanelContextProvider
        isProjectReadOnly={isProjectReadOnly}
        panelBankSectionConfigRef={panelBankSectionConfigRef}
        panelRef={panelRef}
        inheritedSettings={inheritedSettings}>
        <EditablePanelFullScreenWrapper
          currentHeight={0}
          key={panelRef.id}
          shouldRenderContent={shouldRenderContent}
          shouldTurnOnComments={false}
        />
      </PanelContextProvider>
    </SharedPanelStateContextProvider>
  );
};
